import { cn } from "@/lib/utils";
import { useApp } from "@/app/providers/app-provider";
import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useFavoriteTracks } from "@/hooks/use-favorite-tracks";

export const FavoriteButton = ({
	className,
	trackId,
	disabled = false,
}: {
	className?: string;
	trackId: string;
	disabled?: boolean;
}) => {
	const { appStore, favoriteTrackRepository, handleModalOpen } = useApp();
	const userId = appStore.session?.user?.id;
	const { useAddFavorite, useRemoveFavorite, useFavorites } = useFavoriteTracks(
		favoriteTrackRepository,
	);
	const { data: favoriteTrackIds = [] } = useFavorites(userId);
	const addFavorite = useAddFavorite();
	const removeFavorite = useRemoveFavorite();

	const isTrackFavored = (trackId: string) => {
		return favoriteTrackIds.includes(trackId);
	};

	const toggleFavorite = (trackId: string) => {
		if (!userId) {
			handleModalOpen("ACCOUNT_REQUIRED_MODAL", true);
			return;
		}

		if (isTrackFavored(trackId)) {
			// Remove from favorites
			removeFavorite.mutate({ userId, trackId });
		} else {
			// Add to favorites
			addFavorite.mutate({ userId, trackId });
		}
	};

	return (
		<div className={cn(className)}>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							className="rounded-full p-3"
							onClick={() => toggleFavorite(trackId)}
							variant={isTrackFavored(trackId) ? "primary" : "secondary"}
							disabled={disabled}
						>
							<Heart />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Add to Favorites</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</div>
	);
};
